import {
  Combobox as HUICombobox,
  ComboboxButton as HUIComboboxButton,
  ComboboxInput as HUIComboboxInput,
  ComboboxOption as HUIComboboxOption,
  ComboboxOptions as HUIComboboxOptions,
  Label,
  Transition,
} from '@headlessui/react';
import { t } from 'i18next';
import { useCallback, useMemo, useState } from 'react';
import { useDebouncedState } from '../../../hooks/use-debounced-state';
import { cn } from '../../../lib/styles';
import { FieldError } from '../field-error/field-error.spark';
import { Icon } from '../icon/icon.spark';
import { Spinner } from '../spinner/spinner.spark';
import { Tooltip } from '../tooltip/tooltip.spark';
import {
  DEFAULT_SEARCH_DEBOUNCE_MS,
  useRememberedOptions,
  useSearchNotifier,
} from './combobox.shared';
import { matchesTerms, queryTerms } from './filter';
import type { ComboboxProps } from './types';

export function Combobox(props: ComboboxProps) {
  const [query, setQuery] = useState<string>('');
  const isAsync = !!props.onSearch;
  const debouncedQuery = useDebouncedState(
    query,
    props.filterDebounceMs ?? (isAsync ? DEFAULT_SEARCH_DEBOUNCE_MS : 0),
  );
  const filterQuery = query.trim() ? debouncedQuery : query;
  const clearable = !props.multiple && !!props.clearable;
  const allowCustomValue = !props.multiple && !!props.allowCustomValue;

  useSearchNotifier(debouncedQuery, props.onSearch);

  const getOption = useRememberedOptions(props.options);

  const hasQuery = filterQuery.trim().length > 0;
  const noMatchMessage = allowCustomValue
    ? t('labels:no_results_custom_value')
    : t('labels:no_results');
  const emptyMessage = hasQuery ? noMatchMessage : props.noOptionsMessage;

  const filtered = useMemo(() => {
    // In async mode the server has already filtered; re-filtering would hide valid results
    // whenever the server matches on a field we don't have locally (e.g. an email).
    const terms = isAsync ? [] : queryTerms(filterQuery);
    if (terms.length === 0) {
      return props.options;
    }

    return props.options.filter((option) => matchesTerms(terms, option.label, option.value));
  }, [props.options, filterQuery, isAsync]);

  // Headless UI expects a render-prop child whenever `virtual` is set, so the virtualiser has to
  // go away entirely while loading rather than the child being swapped out beneath it.
  const virtual = useMemo(
    () =>
      filtered.length && !props.loading
        ? {
            options: filtered.map((option) => option.value),
            disabled: (value: string | null) => !!(value && getOption(value)?.disabled),
          }
        : null,
    [filtered, getOption, props.loading],
  );

  const { multiple, value, onChange } = props;
  const removeOption = useCallback(
    (valueToRemove: string) => {
      if (!multiple) {
        return;
      }

      if (!value) {
        return;
      }

      const optionsAfterRemoval = value.filter((option) => option !== valueToRemove);

      onChange?.(optionsAfterRemoval);
    },
    [multiple, value, onChange],
  );

  return (
    <div className="flex shrink-0 flex-col gap-2 text-sm text-spark-text-primary">
      <HUICombobox
        as="div"
        value={props.value}
        onChange={(e) => {
          if (props.multiple) {
            props.onChange?.(e === null ? [] : (e as string[]));
          } else if (e === null) {
            // Headless UI emits null when the input is emptied; treat it as a clear only when clearing is allowed.
            if (clearable) {
              props.onChange?.(null);
            }
          } else {
            props.onChange?.(e as string);
          }
        }}
        disabled={props.disabled}
        id={props.id}
        multiple={props.multiple}
        virtual={virtual}
      >
        {({ open }) => (
          <div className="flex flex-col gap-1.5">
            {props.label && (
              <Label className="flex items-center gap-x-1">
                {props.label}
                {props.tooltip && (
                  <Tooltip content={props.tooltip}>
                    <Icon name="infoCircle" />
                  </Tooltip>
                )}
              </Label>
            )}
            <div className="flex flex-col gap-1">
              <div
                className={cn(
                  'flex h-spark-component shrink-0 items-center overflow-clip rounded-lg border border-gray-300 bg-white shadow-xs focus-within:border-spark-purple focus-within:outline-hidden focus-within:ring-spark-purple',
                  {
                    'bg-gray-100 text-spark-text-secondary': props.disabled,
                    'border-red-300 bg-red-100 text-spark-text-danger focus-within:border-spark-text-danger':
                      !!props.error,
                  },
                )}
              >
                <HUIComboboxInput
                  autoComplete="off"
                  className={cn(
                    'w-full border-none bg-transparent px-2 pe-5 text-sm leading-5 outline-hidden placeholder:text-spark-text-secondary focus:ring-0',
                    { 'placeholder:text-spark-text-danger': !!props.error },
                  )}
                  displayValue={(value?: string | string[]) => {
                    // If no value, or multi-select, we want to see the placeholder of the input box
                    if (typeof value !== 'string') {
                      return '';
                    }

                    return getOption(value)?.label ?? (allowCustomValue ? value : '');
                  }}
                  placeholder={props.placeholder}
                  onChange={(e) => {
                    const nextQuery = e.target.value;
                    setQuery(nextQuery);

                    if (!props.multiple && props.allowCustomValue) {
                      props.onChange?.(nextQuery);
                    }
                  }}
                />
                {!props.multiple && props.clearable && props.value && !props.disabled && (
                  <button
                    type="button"
                    name="clear"
                    aria-label={t('aria:clear_selection')}
                    onClick={(event) => {
                      event.stopPropagation();
                      setQuery('');
                      props.onChange?.(null);
                    }}
                    className="flex cursor-pointer items-center pe-1 text-spark-text-secondary"
                  >
                    <Icon name="x" size="xs" />
                  </button>
                )}
                <HUIComboboxButton className="inset-y-0 end-0 flex items-center pe-2 text-spark-text-secondary">
                  <Icon name="chevronDown" />
                </HUIComboboxButton>
              </div>

              <Transition
                show={open}
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                afterLeave={() => setQuery('')}
              >
                <HUIComboboxOptions
                  anchor="bottom start"
                  // Dropdown width must span input + button, hence this class.
                  className="z-30 max-h-60 w-[calc(var(--button-width)+var(--input-width))] overflow-auto rounded-md border border-gray-300 bg-white py-1 text-sm text-spark-text-primary shadow-lg [--anchor-gap:4px] focus:outline-hidden"
                >
                  {virtual ? (
                    ({ option }: { option: string }) => (
                      <HUIComboboxOption
                        value={option}
                        className="w-full cursor-pointer font-normal data-focus:bg-gray-100 data-selected:font-semibold data-disabled:text-spark-text-secondary"
                      >
                        <div className="px-2 py-1">{getOption(option)?.label ?? option}</div>
                      </HUIComboboxOption>
                    )
                  ) : props.loading ? (
                    <div className="flex items-center px-2 py-1 text-spark-text-secondary">
                      <Spinner />
                    </div>
                  ) : (
                    emptyMessage && (
                      <div className="px-2 py-1 text-spark-text-secondary">{emptyMessage}</div>
                    )
                  )}
                </HUIComboboxOptions>
              </Transition>
            </div>
          </div>
        )}
      </HUICombobox>

      {props.error && <FieldError>{props.error}</FieldError>}

      {props.multiple && (
        <div className="flex flex-wrap gap-1">
          {props.value?.map((selected) => {
            const option = getOption(selected);
            return (
              <div
                key={option?.key ?? option?.value}
                className="flex items-center gap-1.5 rounded-md border border-gray-300 bg-gray-100 px-1.5"
              >
                {option?.label}
                <button
                  type="button"
                  name="remove"
                  aria-label={t('aria:remove_option', { option: option?.label })}
                  onClick={() => removeOption(selected)}
                  className="cursor-pointer text-spark-text-secondary"
                >
                  <Icon name="x" size="xs" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
